import { signal } from '@preact/signals-core';
import { activeRoleSignal, getAuthHeaders, customersSignal, pageTypesSignal, searchCustomersForAutocomplete } from './assets';

export const contractsSignal = signal<any[]>([]);
export const selectedContractIdSignal = signal<string | null>(null);
export const contractsSearchQuerySignal = signal<string>('');

export async function refreshContracts() {
	try {
		const res = await fetch('/api/v1/contracts', { headers: getAuthHeaders() });
		if (res.ok) {
			contractsSignal.value = await res.json();
		} else {
			contractsSignal.value = [];
		}
	} catch (e) {
		console.error('Failed to fetch contracts', e);
	}
}

export function renderContractsPage(): string {
	const role = activeRoleSignal.value;
	const isReadOnly = role === 'technician' || role === 'viewer';
	const contracts = contractsSignal.value || [];
	const pageTypes = pageTypesSignal.value || [];

	// Create dynamic rates fields for creation modal
	const createRatesFieldsHtml = pageTypes.map(pt => `
		<s-box padding="small" background="surface-secondary" border="base" borderRadius="base">
			<s-stack gap="small">
				<s-heading element="h4">${pt.displayName || pt.name}</s-heading>
				<input type="hidden" name="rate_pageTypeId_${pt.id}" value="${pt.id}">
				<s-grid gridTemplateColumns="1fr 1fr 1fr" gap="small">
					<s-number-field label="Base Volume" name="rate_baseVolume_${pt.id}" value="0" required></s-number-field>
					<s-number-field label="Base Rate (Rs.)" name="rate_baseRate_${pt.id}" value="0.00" step="0.01" required></s-number-field>
					<s-number-field label="Overage Rate (Rs.)" name="rate_overageRate_${pt.id}" value="0.0000" step="0.0001" required></s-number-field>
				</s-grid>
			</s-stack>
		</s-box>
	`).join('');

	return `
		<s-page heading="Contracts Portal" subtitle="Manage customer agreements, billing cycles, scope, and print tier rates." inlineSize="large">
			${!isReadOnly ? `
				<div slot="primaryAction">
					<s-button variant="primary" id="btn-open-create-contract">Add Contract</s-button>
				</div>
			` : ''}

			<!-- Contracts Table Section -->
			<s-section>
				<s-box padding="none" background="surface" border="base" borderRadius="base">
					${contracts.length === 0 ? `
						<div style="padding: 3rem; text-align: center;">
							<s-text tone="subdued">No contracts registered in system.</s-text>
						</div>
					` : `
						<opm-data-table
							id="contracts-table"
							searchable="true"
							paginated="true"
							page-size="10"
							empty-message="No contracts found."
							search-placeholder="Search contracts..."
						></opm-data-table>
					`}
				</s-box>
			</s-section>

			<!-- Detailed Preview Section -->
			<s-section>
				<div id="contract-preview-container">
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<div style="padding: 2rem; text-align: center; color: var(--p-color-text-subdued);">
							<s-text>Select a contract from the list to view its rates.</s-text>
						</div>
					</s-box>
				</div>
			</s-section>

			<!-- Create Contract Modal -->
			<s-modal id="create-contract-modal" heading="Create Customer Contract">
				<s-form id="form-create-contract">
					<s-stack gap="base">
						<div style="display:flex; flex-direction:column; gap:0.25rem;">
							<label style="font-size:0.875rem; font-weight:500;">Customer Organization</label>
							<opm-autocomplete id="create-contract-customer-select" label="Customer Organization" placeholder="Search customers..."></opm-autocomplete>
							<input type="hidden" name="customerId" id="create-contract-customer-id" />
						</div>

						<div style="display:flex; flex-direction:column; gap:0.25rem;">
							<label style="font-size:0.875rem; font-weight:500;">Billing Cycle</label>
							<select name="billingCycle" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface); height:38px;" required>
								<option value="monthly">Monthly</option>
								<option value="quarterly">Quarterly</option>
								<option value="annually">Annually</option>
							</select>
						</div>

						<div style="display:flex; flex-direction:column; gap:0.25rem;">
							<label style="font-size:0.875rem; font-weight:500;">Scope Type</label>
							<select name="scopeType" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface); height:38px;" required>
								<option value="per_machine">Per Machine (MPS)</option>
								<option value="fleet_wide">Fleet Wide</option>
							</select>
						</div>

						<s-grid gridTemplateColumns="1fr 1fr" gap="small">
							<s-text-field label="Start Date" name="startDate" type="date" placeholder="YYYY-MM-DD" required></s-text-field>
							<s-text-field label="End Date" name="endDate" type="date" placeholder="YYYY-MM-DD" required></s-text-field>
						</s-grid>

						<s-heading element="h3">Contract Rates Configuration</s-heading>
						<s-stack gap="small">
							${createRatesFieldsHtml}
						</s-stack>

						<div id="create-contract-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem; font-weight:500;"></div>
						<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
							<s-button variant="primary" id="btn-submit-create-contract">Create Contract</s-button>
							<s-button commandFor="create-contract-modal" command="--hide">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>

			<!-- Edit Contract Modal -->
			<s-modal id="edit-contract-modal" heading="Edit Customer Contract">
				<s-form id="form-edit-contract">
					<s-stack gap="base">
						<div style="display:flex; flex-direction:column; gap:0.25rem;">
							<label style="font-size:0.875rem; font-weight:500;">Customer Organization</label>
							<opm-autocomplete id="edit-contract-customer-select" label="Customer Organization" placeholder="Search customers..."></opm-autocomplete>
							<input type="hidden" name="customerId" id="edit-contract-customer-id" />
						</div>

						<div style="display:flex; flex-direction:column; gap:0.25rem;">
							<label style="font-size:0.875rem; font-weight:500;">Billing Cycle</label>
							<select name="billingCycle" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface); height:38px;" required>
								<option value="monthly">Monthly</option>
								<option value="quarterly">Quarterly</option>
								<option value="annually">Annually</option>
							</select>
						</div>

						<div style="display:flex; flex-direction:column; gap:0.25rem;">
							<label style="font-size:0.875rem; font-weight:500;">Scope Type</label>
							<select name="scopeType" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface); height:38px;" required>
								<option value="per_machine">Per Machine (MPS)</option>
								<option value="fleet_wide">Fleet Wide</option>
							</select>
						</div>

						<s-grid gridTemplateColumns="1fr 1fr" gap="small">
							<s-text-field label="Start Date" name="startDate" type="date" placeholder="YYYY-MM-DD" required></s-text-field>
							<s-text-field label="End Date" name="endDate" type="date" placeholder="YYYY-MM-DD" required></s-text-field>
						</s-grid>

						<s-heading element="h3">Contract Rates Configuration</s-heading>
						<s-stack gap="small" id="edit-contract-rates-container">
							<!-- Populated dynamically when populated -->
						</s-stack>

						<div id="edit-contract-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem; font-weight:500;"></div>
						<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
							<s-button variant="primary" id="btn-submit-edit-contract">Save Changes</s-button>
							<s-button commandFor="edit-contract-modal" command="--hide">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>
		</s-page>
	`;
}

export function setupContractsPageListeners() {
	const role = activeRoleSignal.value;
	const isReadOnly = role === 'technician' || role === 'viewer';
	const contracts = contractsSignal.value || [];
	const customers = customersSignal.value || [];
	const pageTypes = pageTypesSignal.value || [];

	const customerMap = new Map(customers.map(c => [c.id, c.name]));

	// Update preview if there is a selected contract
	updatePreviewSection();

	// 1. Open Create Modal
	const openCreateBtn = document.getElementById('btn-open-create-contract');
	if (openCreateBtn) {
		openCreateBtn.addEventListener('click', () => {
			const modal = document.getElementById('create-contract-modal') as any;
			const errDiv = document.getElementById('create-contract-error');
			if (errDiv) errDiv.style.display = 'none';

			// Clear fields
			const form = document.getElementById('form-create-contract') as HTMLFormElement;
			if (form) {
				form.querySelectorAll('[name^="rate_baseVolume_"]').forEach((el: any) => el.value = '0');
				form.querySelectorAll('[name^="rate_baseRate_"]').forEach((el: any) => el.value = '0.00');
				form.querySelectorAll('[name^="rate_overageRate_"]').forEach((el: any) => el.value = '0.0000');
				const startEl = form.querySelector('[name="startDate"]') as any;
				const endEl = form.querySelector('[name="endDate"]') as any;
				if (startEl) startEl.value = '';
				if (endEl) endEl.value = '';
			}

			if (modal && typeof modal.showOverlay === 'function') {
				modal.showOverlay();
			}
		});
	}

	const createCustAutocomplete = document.getElementById('create-contract-customer-select') as any;
	if (createCustAutocomplete) {
		createCustAutocomplete.loadItems = searchCustomersForAutocomplete;
		createCustAutocomplete.addEventListener('select', (e: CustomEvent) => {
			const hidden = document.getElementById('create-contract-customer-id') as HTMLInputElement | null;
			if (hidden) hidden.value = e.detail.id || '';
		});
	}

	// Helper to extract rates config from form
	const getRatesFromForm = (form: HTMLFormElement): any[] => {
		const rates: any[] = [];
		pageTypes.forEach(pt => {
			const pageTypeIdEl = form.querySelector(`[name="rate_pageTypeId_${pt.id}"]`) as HTMLInputElement;
			const baseVolumeEl = form.querySelector(`[name="rate_baseVolume_${pt.id}"]`) as HTMLInputElement;
			const baseRateEl = form.querySelector(`[name="rate_baseRate_${pt.id}"]`) as HTMLInputElement;
			const overageRateEl = form.querySelector(`[name="rate_overageRate_${pt.id}"]`) as HTMLInputElement;

			if (pageTypeIdEl && baseVolumeEl && baseRateEl && overageRateEl) {
				rates.push({
					pageTypeId: pageTypeIdEl.value,
					baseVolume: Number(baseVolumeEl.value || 0),
					baseRate: Number(baseRateEl.value || 0),
					overageRate: Number(overageRateEl.value || 0)
				});
			}
		});
		return rates;
	};

	// 2. Submit Create Form
	const submitCreateBtn = document.getElementById('btn-submit-create-contract');
	if (submitCreateBtn) {
		submitCreateBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const form = document.getElementById('form-create-contract') as HTMLFormElement;
			if (!form) return;

			const customerId = (document.getElementById('create-contract-customer-id') as HTMLInputElement | null)?.value || '';
			const billingCycle = (form.querySelector('[name="billingCycle"]') as HTMLSelectElement)?.value || 'monthly';
			const scopeType = (form.querySelector('[name="scopeType"]') as HTMLSelectElement)?.value || 'per_machine';
			const startDate = (form.querySelector('[name="startDate"]') as any)?.value || '';
			const endDate = (form.querySelector('[name="endDate"]') as any)?.value || '';
			const rates = getRatesFromForm(form);

			const errDiv = document.getElementById('create-contract-error');
			if (errDiv) errDiv.style.display = 'none';

			// Client-side validations
			if (!startDate || !endDate) {
				if (errDiv) {
					errDiv.textContent = 'Start and End dates are required.';
					errDiv.style.display = 'block';
				}
				return;
			}
			if (endDate < startDate) {
				if (errDiv) {
					errDiv.textContent = 'End Date cannot be before Start Date.';
					errDiv.style.display = 'block';
				}
				return;
			}

			// Validate rate numbers
			for (const r of rates) {
				if (r.baseVolume < 0 || r.baseRate < 0 || r.overageRate < 0) {
					if (errDiv) {
						errDiv.textContent = 'Base Volume, Base Rate, and Overage Rate must be non-negative.';
						errDiv.style.display = 'block';
					}
					return;
				}
			}

			try {
				const res = await fetch('/api/v1/contracts', {
					method: 'POST',
					headers: getAuthHeaders(),
					body: JSON.stringify({
						customerId,
						billingCycle,
						scopeType,
						startDate,
						endDate,
						rates
					})
				});

				if (res.ok) {
					const modal = document.getElementById('create-contract-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshContracts();
					triggerAppReRender();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to create contract.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}

	const editCustAutocomplete = document.getElementById('edit-contract-customer-select') as any;
	if (editCustAutocomplete) {
		editCustAutocomplete.loadItems = searchCustomersForAutocomplete;
		editCustAutocomplete.addEventListener('select', (e: CustomEvent) => {
			const hidden = document.getElementById('edit-contract-customer-id') as HTMLInputElement | null;
			if (hidden) hidden.value = e.detail.id || '';
		});
	}

	// 3. Configure OpmDataTable
	const table = document.getElementById('contracts-table') as any;
	if (table) {
		table.columns = [
			{ key: 'customerId', header: 'Customer', sortable: true, sortType: 'string', render: (row: any) => customerMap.get(row.customerId) || 'Unknown' },
			{
				key: 'billingCycle',
				header: 'Billing Cycle',
				sortable: true,
				sortType: 'string',
				render: (row: any) => {
					let tone = 'info';
					if (row.billingCycle === 'quarterly') tone = 'attention';
					if (row.billingCycle === 'annually') tone = 'success';
					return `<s-badge tone="${tone}">${row.billingCycle.toUpperCase()}</s-badge>`;
				}
			},
			{ key: 'startDate', header: 'Start Date', sortable: true, sortType: 'string' },
			{ key: 'endDate', header: 'End Date', sortable: true, sortType: 'string' },
			{
				key: 'scopeType',
				header: 'Scope Type',
				sortable: true,
				sortType: 'string',
				render: (row: any) => {
					const label = row.scopeType === 'per_machine' ? 'Per Machine' : 'Fleet Wide';
					return `<s-badge tone="info">${label}</s-badge>`;
				}
			},
			{
				key: 'status',
				header: 'Status',
				render: (row: any) => {
					const now = new Date();
					const todayStr = now.toISOString().split('T')[0] || '';

					let status = 'active';
					let tone = 'success';
					if (todayStr < row.startDate) {
						status = 'pending';
						tone = 'attention';
					} else if (todayStr > row.endDate) {
						status = 'expired';
						tone = 'critical';
					}
					return `<s-badge tone="${tone}">${status.toUpperCase()}</s-badge>`;
				}
			},
			{
				key: 'actions',
				header: 'Actions',
				render: (row: any) => {
					return `
							<s-button data-action="view-rates" data-id="${row.id}" size="slim">View Rates</s-button>
							${!isReadOnly ? `
								<s-button data-action="edit" data-id="${row.id}" size="slim">Edit</s-button>
							` : ''}
					`;
				}
			}
		];
		table.items = contracts;

		table.addEventListener('row-action', (e: CustomEvent) => {
			const { action, id } = e.detail;
			selectedContractIdSignal.value = id;

			if (action === 'view-rates') {
				updatePreviewSection();
				// Scroll to preview container
				const container = document.getElementById('contract-preview-container');
				if (container) {
					container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
				}
			} else if (action === 'edit') {
				const contract = contracts.find(x => x.id === id);
				if (!contract) return;

				populateEditForm(contract);

				const errDiv = document.getElementById('edit-contract-error');
				if (errDiv) errDiv.style.display = 'none';

				const modal = document.getElementById('edit-contract-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			}
		});
	}

	// 4. Submit Edit Form
	const submitEditBtn = document.getElementById('btn-submit-edit-contract');
	if (submitEditBtn) {
		submitEditBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const id = selectedContractIdSignal.value;
			if (!id) return;

			const form = document.getElementById('form-edit-contract') as HTMLFormElement;
			if (!form) return;

			const customerId = (document.getElementById('edit-contract-customer-id') as HTMLInputElement | null)?.value || '';
			const billingCycle = (form.querySelector('[name="billingCycle"]') as HTMLSelectElement)?.value || 'monthly';
			const scopeType = (form.querySelector('[name="scopeType"]') as HTMLSelectElement)?.value || 'per_machine';
			const startDate = (form.querySelector('[name="startDate"]') as any)?.value || '';
			const endDate = (form.querySelector('[name="endDate"]') as any)?.value || '';
			const rates = getRatesFromForm(form);

			const errDiv = document.getElementById('edit-contract-error');
			if (errDiv) errDiv.style.display = 'none';

			// Client-side validations
			if (!startDate || !endDate) {
				if (errDiv) {
					errDiv.textContent = 'Start and End dates are required.';
					errDiv.style.display = 'block';
				}
				return;
			}
			if (endDate < startDate) {
				if (errDiv) {
					errDiv.textContent = 'End Date cannot be before Start Date.';
					errDiv.style.display = 'block';
				}
				return;
			}

			// Validate rate numbers
			for (const r of rates) {
				if (r.baseVolume < 0 || r.baseRate < 0 || r.overageRate < 0) {
					if (errDiv) {
						errDiv.textContent = 'Base Volume, Base Rate, and Overage Rate must be non-negative.';
						errDiv.style.display = 'block';
					}
					return;
				}
			}

			try {
				const res = await fetch(`/api/v1/contracts/${id}`, {
					method: 'PUT',
					headers: getAuthHeaders(),
					body: JSON.stringify({
						customerId,
						billingCycle,
						scopeType,
						startDate,
						endDate,
						rates
					})
				});

				if (res.ok) {
					const modal = document.getElementById('edit-contract-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshContracts();
					triggerAppReRender();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to update contract.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}
}

function updatePreviewSection() {
	const container = document.getElementById('contract-preview-container');
	if (!container) return;

	const id = selectedContractIdSignal.value;
	if (!id) {
		container.innerHTML = `
			<s-box padding="base" background="surface" border="base" borderRadius="base">
				<div style="padding: 2rem; text-align: center; color: var(--p-color-text-subdued);">
					<s-text>Select a contract from the list to view its rates.</s-text>
				</div>
			</s-box>
		`;
		return;
	}

	const contract = contractsSignal.value.find(c => c.id === id);
	if (!contract) {
		container.innerHTML = `
			<s-box padding="base" background="surface" border="base" borderRadius="base">
				<div style="padding: 2rem; text-align: center; color: var(--p-color-text-critical);">
					<s-text>Contract not found.</s-text>
				</div>
			</s-box>
		`;
		return;
	}

	const customerMap = new Map((customersSignal.value || []).map(c => [c.id, c.name]));
	const custName = customerMap.get(contract.customerId) || 'Unknown Customer';
	const pageTypeMap = new Map((pageTypesSignal.value || []).map(p => [p.id, p.displayName || p.name]));

	const ratesHtml = (contract.rates || []).map((r: any) => {
		const ptName = pageTypeMap.get(r.pageTypeId) || r.pageTypeId;
		return `
			<s-table-row>
				<s-table-cell><strong>${ptName}</strong></s-table-cell>
				<s-table-cell>${Number(r.baseVolume).toLocaleString()}</s-table-cell>
				<s-table-cell>Rs. ${Number(r.baseRate).toFixed(2)}</s-table-cell>
				<s-table-cell>Rs. ${Number(r.overageRate).toFixed(4)}</s-table-cell>
			</s-table-row>
		`;
	}).join('');

	container.innerHTML = `
		<s-box padding="base" background="surface" border="base" borderRadius="base">
			<s-stack gap="base">
				<s-heading element="h3">Contract Rates: ${custName}</s-heading>
				<s-text color="subdued">Scope Type: ${contract.scopeType === 'per_machine' ? 'Per Machine' : 'Fleet Wide'} | Billing Cycle: ${contract.billingCycle.toUpperCase()}</s-text>
				
				<s-table>
					<s-table-header-row>
						<s-table-header>Page Type</s-table-header>
						<s-table-header>Base Volume</s-table-header>
						<s-table-header>Base Rate</s-table-header>
						<s-table-header>Overage Rate (per page)</s-table-header>
					</s-table-header-row>
					<s-table-body>
						${ratesHtml.length === 0 ? `
							<s-table-row>
								<s-table-cell colspan="4" style="text-align: center; padding: 1rem;">No rates configured for this contract.</s-table-cell>
							</s-table-row>
						` : ratesHtml}
					</s-table-body>
				</s-table>
			</s-stack>
		</s-box>
	`;
}

function populateEditForm(contract: any) {
	const form = document.getElementById('form-edit-contract') as HTMLFormElement;
	if (!form) return;

	const custIdInput = document.getElementById('edit-contract-customer-id') as HTMLInputElement | null;
	if (custIdInput) custIdInput.value = contract.customerId;
	const custAutocomplete = document.getElementById('edit-contract-customer-select') as any;
	if (custAutocomplete) custAutocomplete.selectedId = contract.customerId;

	const billingCycleSelect = form.querySelector('[name="billingCycle"]') as HTMLSelectElement;
	if (billingCycleSelect) billingCycleSelect.value = contract.billingCycle;

	const scopeTypeSelect = form.querySelector('[name="scopeType"]') as HTMLSelectElement;
	if (scopeTypeSelect) scopeTypeSelect.value = contract.scopeType;

	const startDateInput = form.querySelector('[name="startDate"]') as any;
	if (startDateInput) startDateInput.value = contract.startDate;

	const endDateInput = form.querySelector('[name="endDate"]') as any;
	if (endDateInput) endDateInput.value = contract.endDate;

	const container = document.getElementById('edit-contract-rates-container');
	if (container) {
		const pageTypes = pageTypesSignal.value || [];
		container.innerHTML = pageTypes.map(pt => {
			const rate = (contract.rates || []).find((r: any) => r.pageTypeId === pt.id) || {
				baseVolume: 0,
				baseRate: 0,
				overageRate: 0
			};
			return `
				<s-box padding="small" background="surface-secondary" border="base" borderRadius="base">
					<s-stack gap="small">
						<s-heading element="h4">${pt.displayName || pt.name}</s-heading>
						<input type="hidden" name="rate_pageTypeId_${pt.id}" value="${pt.id}">
						<s-grid gridTemplateColumns="1fr 1fr 1fr" gap="small">
							<s-number-field label="Base Volume" name="rate_baseVolume_${pt.id}" value="${rate.baseVolume}" required></s-number-field>
							<s-number-field label="Base Rate (Rs.)" name="rate_baseRate_${pt.id}" value="${rate.baseRate.toFixed(2)}" step="0.01" required></s-number-field>
							<s-number-field label="Overage Rate (Rs.)" name="rate_overageRate_${pt.id}" value="${rate.overageRate.toFixed(4)}" step="0.0001" required></s-number-field>
						</s-grid>
					</s-stack>
				</s-box>
			`;
		}).join('');
	}
}

function triggerAppReRender() {
	const ev = new HashChangeEvent('hashchange');
	window.dispatchEvent(ev);
}
